import * as S from "./styled";
import { MdVerified } from "react-icons/md";
import DefaultUserImg from "../../Assets/Images/user-profile.png";
import { ProtectedImage } from "../ProtectedMedia";
import { SubscribeToDetails } from "../../Config/interface.config";
import { Capitalize, getCreatorProfilePath } from "../../Utils";
import { useNavigate } from "react-router-dom";
import APIRequest from "../../API";
import toast from "react-hot-toast";
import * as APIPATHS from "../../API/path";
import { useState } from "react";
import ConfirmationModal from "../../Modals/ConfirmationModal";
import SubscriptionStatusDisplay from "../SubscriptionStatusDisplay";
import { getSubscriptionId } from "../../Config/subscription.types";

const CANCELLATION_SUBMISSION_FAILED_MESSAGE =
  "Cancellation could not be submitted for this membership. Please contact support.";
interface Props {
  creator: SubscribeToDetails;
  onCancelSuccess: () => void;
}

const UserMemberShip = ({ creator, onCancelSuccess }: Props) => {
  const navigate = useNavigate();
  const [confirmModal, setConfirmModal] = useState(false);
  const [cancelConfirmationStep, setCancelConfirmationStep] = useState<1 | 2>(1);
  const [isCancelling, setIsCancelling] = useState(false);
  const effectiveScheduledCancelAt =
    creator?.scheduledCancelAt ??
    creator?.subscribeDetails?.scheduledCancelAt ??
    null;
  const hasScheduledCancel =
    Boolean(effectiveScheduledCancelAt) ||
    creator?.isScheduledCancel ||
    creator?.subscribeDetails?.isScheduledCancel ||
    creator?.subscribeDetails?.status === "scheduled_cancel";

  const showProfile = (username: string) => {
    navigate(getCreatorProfilePath(username));
  };

  const cancelSubscription = async (creator: SubscribeToDetails) => {
    try {
      setIsCancelling(true);

      const subscriptionId =
        getSubscriptionId(creator.subscribeDetails) ||
        getSubscriptionId(creator as any);

      if (!subscriptionId) {
        throw new Error("Missing subscription ID");
      }

      const response: any = await APIRequest(APIPATHS.CancelSubscription, {
        creatorId: creator._id || creator.id,
        subscriptionId,
        subscription_id: subscriptionId,
      });

      if (response?.error) {
        throw response.error;
      }

      const submitted =
        response?.data?.data?.providerCancellationSubmitted ??
        response?.data?.providerCancellationSubmitted ??
        response?.data?.status === true;

      if (!submitted) {
        throw new Error(CANCELLATION_SUBMISSION_FAILED_MESSAGE);
      }

      toast.success("Cancellation request submitted. Your membership stays active until the current billing period ends.");
      onCancelSuccess();
    } catch (err: any) {
      const safeMessage =
        err?.code === "CANCELLATION_SUBMISSION_FAILED" ||
        err?.status === 400 ||
        err?.status === 500
          ? CANCELLATION_SUBMISSION_FAILED_MESSAGE
          : err?.message || CANCELLATION_SUBMISSION_FAILED_MESSAGE;
      toast.error(safeMessage);
      console.error(err);
    } finally {
      setIsCancelling(false);
    }
  };

  const creatorName = creator?.fullname || creator?.username || "this creator";

  const closeConfirmModal = () => {
    setConfirmModal(false);
    setCancelConfirmationStep(1);
  };

  const toggleConfirmModal = () => {
    setConfirmModal((current) => {
      if (current) setCancelConfirmationStep(1);
      return !current;
    });
  };

  const onConfirm = async (isConfirm: boolean) => {
    if (!isConfirm) {
      closeConfirmModal();
      return;
    }

    if (cancelConfirmationStep === 1) {
      setCancelConfirmationStep(2);
      return;
    }

    closeConfirmModal();
    cancelSubscription(creator);
  };

  return (
    <>
      <S.SubscribedUser>
        <div className="creator-details">
          <ProtectedImage
            as={S.ProfileImage}
            onClick={() => showProfile(creator.username)}
            url={creator?.image?.fileURL}
            fallbackSrc={DefaultUserImg}
            alt=""
          />
          <div>
            <h3 className="flex items-center gap-2">
              {Capitalize(creator?.fullname)} <MdVerified />
            </h3>
            <p>@{creator?.username}</p>
            <SubscriptionStatusDisplay
              subscribeDetails={creator.subscribeDetails}
              scheduledCancelAt={effectiveScheduledCancelAt}
              onCancel={toggleConfirmModal}
              isCancelling={isCancelling}
              showCancelButton={!hasScheduledCancel}
            />
          </div>
        </div>
      </S.SubscribedUser>
      <ConfirmationModal
        onHide={closeConfirmModal}
        show={confirmModal}
        setConfirm={(isConfirm) => onConfirm(isConfirm)}
        title={cancelConfirmationStep === 1 ? "Cancel membership?" : "Confirm cancellation"}
        body={
          cancelConfirmationStep === 1
            ? "Are you sure you want to cancel your membership?"
            : `You'll lose access to ${creatorName}'s premium membership once your current billing period ends.`
        }
        cancelButtonText={cancelConfirmationStep === 1 ? "Keep membership" : "Go back"}
        confirmButtonText={cancelConfirmationStep === 1 ? "Continue" : "Yes, cancel"}
      />
    </>
  );
};

export default UserMemberShip;
